/*
 * Ab wann ist ein Wert veraltet? — eine Schwelle pro Datenpunkt.
 *
 * Vorher stand hier eine Konstante von 60 Sekunden. Die ist im Feld falsch,
 * und zwar in beide Richtungen: ein Zähler, der alle 15 Minuten gepollt
 * wird, wäre dauerhaft grau, und ein HMI, das grundsätzlich grau aussieht,
 * wird ignoriert — dann ist die Alterungsanzeige schlimmer als keine, weil
 * niemand mehr hinsieht, wenn sie recht hat.
 *
 * ----------------------------------------------------------------------
 * Der Kern: Poll-Intervall ist NICHT die Zusage, dass ein Wert ankommt.
 * ----------------------------------------------------------------------
 *
 * Der Feld-Client publisht mit Deadband (docs/hvacloud-client-spec.md
 * §5.2: "publish nur wenn der neue Wert mehr als Deadband vom letzten
 * publish'ten Wert abweicht"). Ein Datenpunkt, der sich nicht ändert,
 * erzeugt also GAR KEINE Nachricht — egal wie oft er gepollt wird. Ein
 * Vorlauf-Sollwert, der seit drei Stunden auf 42,0 steht, ist vollkommen
 * gesund und trotzdem drei Stunden ohne Nachricht.
 *
 * Damit gilt:
 *
 *   - pollIntervalSeconds sagt, wie schnell eine ÄNDERUNG sichtbar wird.
 *     Es sagt nichts darüber, wann die nächste Nachricht kommt.
 *   - heartbeatSeconds ist die einzige Größe, die eine Nachricht auch ohne
 *     Änderung zusagt. Wo sie gesetzt ist, ist sie die Wahrheit.
 *   - Wo sie NICHT gesetzt ist, gibt es im Wire-Protokoll überhaupt keine
 *     zugesagte Kadenz. Dann kann Alter nur noch eine Lebenszeichen-Grenze
 *     sein ("der Client ist weg"), keine Frische-Aussage.
 *
 * Genau daran hängt, welcher der beiden Werte greift — siehe
 * {@link staleAfterMsFor}.
 *
 * ----------------------------------------------------------------------
 * Belegte Herkunft der Zahlen (2026-09-03, gegen den Server-Quellcode)
 * ----------------------------------------------------------------------
 *
 * Der Auftrag nannte "BindingProfile.pollInterval, Default 00:00:05" und
 * "heartbeat, Default 00:10:00". Beides steht so nicht im Repo, deshalb
 * hier die geprüften Fakten:
 *
 *   - Einen Typ BindingProfile gibt es nicht. Das Feld heisst
 *     AcquisitionProfile.PollInterval
 *     (src/shared/HVACloud.Contracts.Discovery/OperationalMap.cs:65-72),
 *     gefüllt aus Leaf.PollIntervalSeconds
 *     (src/shared/HVACloud.Model/Leaf.cs:110).
 *   - Die 5 Sekunden sind KEIN Default des Vertrags, sondern ein
 *     treiberseitiger Fallback, einer pro Treiber und per Konfiguration
 *     überschreibbar (BacnetIpDriverOptions.cs:38,
 *     ClimatixDriverOptions.cs:72, OpcUaDriverOptions.cs:50,
 *     KnxIpDriverOptions.cs:60, HttpJsonDriverOptions.cs:92). Der
 *     Modellkommentar sagt dazu: "null lets the driver pick its default".
 *     Wir übernehmen die 5 s als Annahme, weil alle fünf Treiber sie
 *     teilen — aber als Annahme, nicht als Zusage.
 *   - Einen Heartbeat-Default von 10 Minuten gibt es nicht. Leaf.cs:112
 *     sagt ausdrücklich: "null **disables** the heartbeat channel". Der
 *     10-Minuten-Wert im Auftrag ist vermutlich mit
 *     HeartbeatOptions.Interval verwechselt — das ist der
 *     Client-Lebenszeichen-Heartbeat, steht auf 5 Minuten
 *     (HeartbeatReporter.cs:221) und betrifft den Client, nicht einen
 *     Datenpunkt.
 *   - NACHTRAG 2026-09-09: der Satz "der Kanal ist nirgends implementiert",
 *     der hier bis zum 03.09. stand, gilt nicht mehr. Das CoV-Gate liest
 *     AcquisitionProfile.Heartbeat seit Telemetrie-Phase 1b
 *     (CovGate.Decide, Zweig PublishDecision.Heartbeat; Abbildung in
 *     CovProfile.cs), in beiden Field-Clients. Und seit der Migration
 *     SetDefaultHeartbeatForRecordedLeaves_20260909 trägt jeder
 *     aufgezeichnete Datenpunkt 900 s. Fall 1 des Entscheidungsbaums unten
 *     ist damit der Normalfall und nicht die Ausnahme: Schwelle 45 Minuten
 *     statt NO_HEARTBEAT_FALLBACK_MS.
 *
 * Konsequenz für diesen Code: wo heartbeatSeconds gesetzt ist, wird es
 * respektiert (ein Dritt-Client kann den Kanal bedienen, und das Schema
 * trägt das Feld). Wo es fehlt, wird NICHT auf zehn Minuten geraten,
 * sondern der Fall "keine zugesagte Kadenz" ausdrücklich behandelt.
 *
 * Modul ohne Laufzeit-Importe (nur Typen), damit es ohne Bundler direkt
 * testbar bleibt — siehe tests/staleness.test.ts.
 */

/**
 * Was die HMI-Schicht über einen Datenpunkt wissen muss.
 *
 * Feldnamen und Nullability spiegeln `LeafDto` (`api/types.ts`), damit die
 * produktive Route das Anlagen-Schema unverändert durchreichen kann.
 */
export interface HmiLeafInfo {
  /** Anzeigename für Command-Toasts. Ohne das steht dort der Guid. */
  label?: string;
  /** Aus `Leaf.PollIntervalSeconds`. Null = Treiber-Default. */
  pollIntervalSeconds?: number | null;
  /** Aus `Leaf.HeartbeatSeconds`. Null oder 0 = Kanal deaktiviert. */
  heartbeatSeconds?: number | null;
}

/**
 * Wie viele erwartete Zyklen dürfen ausfallen, bevor wir es einen Befund
 * nennen?
 *
 * Drei. Ein verpasster Zyklus ist Rauschen (Netz-Jitter, ein langsamer
 * Poll-Durchlauf mit vielen Datenpunkten, ein GC-Hicks im Client), zwei
 * sind Pech, drei sind ein Muster. Die Zahl steht hier EINMAL — wer sie
 * ändern will, ändert sie hier und nirgends sonst.
 */
export const MISSED_CYCLE_FACTOR = 3;

/**
 * Angenommenes Poll-Intervall, wenn der Datenpunkt keines trägt. Geteilter
 * Fallback aller fünf Treiber-Options (Belege im Modul-Header). Annahme,
 * nicht Zusage: ein Treiber kann per Konfiguration etwas anderes fahren,
 * und die Cloud erfährt das nicht.
 */
export const ASSUMED_POLL_SECONDS = 5;

/**
 * Schwelle für den Fall "keine zugesagte Kadenz" — also kein Heartbeat auf
 * dem Datenpunkt.
 *
 * Zehn Minuten, und das ist bewusst eine LEBENSZEICHEN-Grenze und keine
 * Frische-Aussage. Herleitung aus dem gröbsten Lebenszeichen, das das
 * System überhaupt hat: der Client meldet sich alle fünf Minuten
 * (`HeartbeatReporter.cs:221`); nach zwei ausgefallenen Meldungen ist er mit
 * hoher Wahrscheinlichkeit wirklich weg. Ein Wert, der länger als das nicht
 * nachgekommen ist, kann bei aktivem Deadband gesund sein — aber er kann
 * auch von einem toten Feld-Client stammen, und diese zwei Fälle sind ohne
 * Heartbeat am Datenpunkt clientseitig NICHT unterscheidbar.
 *
 * Warum nicht einfach gar nicht altern? Weil der Hub-Zustand nur die
 * Strecke Browser–Server abdeckt. Stirbt der Feld-Client, bleibt der Hub
 * verbunden, der Server hört nur auf zu pushen, und ein eingefrorener Wert
 * sähe für immer frisch aus. Das ist der teurere Fehler.
 *
 * Der richtige Fix liegt nicht hier: er heisst `heartbeatSeconds` am
 * Datenpunkt setzen. Genau das ist am 2026-09-09 passiert (Migration
 * SetDefaultHeartbeatForRecordedLeaves_20260909, 900 s), und der Kanal wird
 * seit Telemetrie-Phase 1b von beiden Field-Clients bedient. Dieser Fallback
 * greift damit nur noch für Datenpunkte, an denen jemand den Heartbeat
 * ausdrücklich geleert hat, und für nicht aufgezeichnete Punkte — die
 * publizieren ohnehin nicht.
 */
export const NO_HEARTBEAT_FALLBACK_MS = 10 * 60_000;

/** Untergrenze. Ohne die würde ein Datenpunkt mit 2-s-Heartbeat bei
 *  normalem Netz-Jitter flackern. */
export const MIN_STALE_AFTER_MS = 15_000;

/** Obergrenze. Fängt einen Unsinnswert aus dem Schema (Poll = 2^31 s) ab,
 *  bevor daraus eine Schwelle wird, die nie greift. */
export const MAX_STALE_AFTER_MS = 24 * 3_600_000;

/**
 * Schwelle, ab der ein Wert dieses Datenpunkts als veraltet gilt.
 *
 * Entscheidungsbaum — das ist die Antwort auf "welcher der beiden Werte
 * greift in welchem Fall":
 *
 *   1. **Heartbeat gesetzt** (> 0): er ist die zugesagte Kadenz, also
 *      zählt nur er. Schwelle = Heartbeat × {@link MISSED_CYCLE_FACTOR}.
 *      Zusätzlich wird nach unten gegen `Heartbeat + Poll` geklammert: die
 *      Schwelle darf NIEMALS unter dem Heartbeat liegen, sonst dimmt ein
 *      völlig gesunder Datenpunkt zwischen zwei Heartbeats. Mit Faktor 3
 *      ist das ohnehin erfüllt; die Klammer hält es auch, wenn jemand den
 *      Faktor auf 1 stellt.
 *   2. **Kein Heartbeat**: es gibt keine zugesagte Kadenz. Schwelle =
 *      {@link NO_HEARTBEAT_FALLBACK_MS}, nach unten aber nie kleiner als
 *      Poll × Faktor — ein Datenpunkt mit 15-Minuten-Poll bekommt so 45
 *      Minuten und nicht die zehn. Das Poll-Intervall kann die Schwelle
 *      hier also nur ANHEBEN, niemals senken; senken darf es nicht, weil
 *      der Deadband dazwischensteht (siehe Modul-Header).
 *   3. Ergebnis auf [{@link MIN_STALE_AFTER_MS}, {@link MAX_STALE_AFTER_MS}]
 *      geklammert.
 *
 * Ohne jede Information (kein Schema geladen, Datenpunkt unbekannt) greift
 * Fall 2 mit dem angenommenen Poll-Intervall — also die zehn Minuten.
 */
export function staleAfterMsFor(info: HmiLeafInfo | null | undefined): number {
  const heartbeatMs = positiveSecondsToMs(info?.heartbeatSeconds);
  const pollMs = positiveSecondsToMs(info?.pollIntervalSeconds) ?? ASSUMED_POLL_SECONDS * 1000;

  const threshold = heartbeatMs !== null
    // Fall 1: zugesagte Kadenz vorhanden.
    ? Math.max(heartbeatMs * MISSED_CYCLE_FACTOR, heartbeatMs + pollMs)
    // Fall 2: keine zugesagte Kadenz.
    : Math.max(NO_HEARTBEAT_FALLBACK_MS, pollMs * MISSED_CYCLE_FACTOR);

  return clamp(threshold, MIN_STALE_AFTER_MS, MAX_STALE_AFTER_MS);
}

/**
 * Erklärt die Schwelle auf Deutsch — für den Tooltip an der Kachel.
 *
 * Der Grund für diese Funktion: eine gedimmte Kachel ohne Begründung macht
 * den Techniker nicht handlungsfähig. "Heartbeat 60 s, veraltet ab 3 min"
 * sagt ihm, ob er die Anlage oder die Konfiguration anschauen muss.
 */
export function describeStalePolicyDe(info: HmiLeafInfo | null | undefined): string {
  const heartbeatMs = positiveSecondsToMs(info?.heartbeatSeconds);
  const pollMs = positiveSecondsToMs(info?.pollIntervalSeconds);
  const threshold = staleAfterMsFor(info);
  if (heartbeatMs !== null) {
    return `Heartbeat ${formatDurationDe(heartbeatMs)}, veraltet ab ${formatDurationDe(threshold)}`;
  }
  const pollText = pollMs !== null
    ? `Poll ${formatDurationDe(pollMs)}`
    : `Poll unbekannt (angenommen ${formatDurationDe(ASSUMED_POLL_SECONDS * 1000)})`;
  return `${pollText}, kein Heartbeat — veraltet ab ${formatDurationDe(threshold)}`;
}

/** Grobe Dauer-Formatierung für Tooltips. */
export function formatDurationDe(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 90) return `${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} h`;
  return `${Math.round(hours / 24)} Tage`;
}

/** Sekunden → ms, aber nur für echte positive Zahlen. Null, 0, negative
 *  Werte und NaN heissen alle "nicht gesetzt": `Leaf.cs:112` definiert null
 *  als deaktiviert, und 0 oder negativ ist als Kadenz sinnlos. */
function positiveSecondsToMs(seconds: number | null | undefined): number | null {
  if (typeof seconds !== 'number') return null;
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return seconds * 1000;
}

function clamp(value: number, lo: number, hi: number): number {
  return value < lo ? lo : value > hi ? hi : value;
}
